import { useEffect, useRef, useState } from 'react';
import {
  useProjectSearchSource,
  useWorkerSearchSource,
} from '../hooks/useEntitySearchSource';

/** An id + human label pair — what the filter bar keeps for a chosen worker or project. */
export interface SelectedEntity {
  id: number;
  label: string;
}

/**
 * Everything about *what* is being searched, with none of the chrome. Produced by
 * {@link useWorkerSearchSource} / {@link useProjectSearchSource} and consumed by both the
 * desktop popover and the mobile filters sheet, so the two never drift apart.
 */
export interface EntitySearchSource {
  searchPlaceholder: string;
  term: string;
  onTermChange: (t: string) => void;
  /** Search results for the current term, already mapped to id + label. */
  results: SelectedEntity[];
  isLoading: boolean;
  error: unknown;
  /** Shown when a search returns nothing. */
  emptyState: string;
  /** True when more results exist than are shown, so the user knows to narrow the term. */
  hasMore: boolean;
}

export interface EntityMultiSelectBodyProps extends EntitySearchSource {
  /** Control label, e.g. "Workers". Used for the "Clear workers" action. */
  label: string;
  selected: SelectedEntity[];
  onChange: (next: SelectedEntity[]) => void;
  /** Focus the search box on mount. The popover wants this; an inline section does not. */
  autoFocus?: boolean;
}

/**
 * The guts of the entity picker: a debounced search box, chips for what's chosen, a
 * toggle-on-click result list, and a clear-all action. No container, no trigger — the
 * caller supplies those, which is what lets the desktop dropdown
 * ({@link WorkerFilterSelect}) and the mobile filters sheet render the same control in two
 * very different shells.
 */
export function EntityMultiSelectBody({
  label,
  selected,
  onChange,
  searchPlaceholder,
  term,
  onTermChange,
  results,
  isLoading,
  error,
  emptyState,
  hasMore,
  autoFocus = false,
}: EntityMultiSelectBodyProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedIds = new Set(selected.map((s) => s.id));

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  function toggle(item: SelectedEntity) {
    onChange(
      selectedIds.has(item.id)
        ? selected.filter((s) => s.id !== item.id)
        : [...selected, item],
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        type="search"
        value={term}
        onChange={(e) => onTermChange(e.target.value)}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        className="w-full border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
      />

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2 pb-2 border-b border-slate-200">
          {selected.map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1 bg-brand-blue/10 text-brand-blue text-xs rounded-full pl-2.5 pr-1 py-1"
            >
              <span className="max-w-[180px] truncate">{s.label}</span>
              <button
                type="button"
                onClick={() => onChange(selected.filter((c) => c.id !== s.id))}
                aria-label={`Remove ${s.label}`}
                className="hover:text-brand-blue/70 leading-none text-sm"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="max-h-64 overflow-y-auto mt-2">
        {isLoading && <p className="text-xs text-slate-500 px-1 py-2">Searching…</p>}
        {!!error && !isLoading && (
          <p role="alert" className="text-xs text-red-600 px-1 py-2">
            Could not run the search. Try again.
          </p>
        )}
        {!isLoading && !error && results.length === 0 && (
          <p className="text-xs text-slate-500 px-1 py-2">{emptyState}</p>
        )}
        {!isLoading && !error &&
          results.map((r) => {
            const isSelected = selectedIds.has(r.id);
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => toggle(r)}
                aria-pressed={isSelected}
                className={`w-full text-left text-sm px-2 py-1.5 rounded flex items-center gap-2 ${
                  isSelected ? 'bg-brand-blue/10 text-brand-blue' : 'text-brand-charcoal hover:bg-slate-50'
                }`}
              >
                <span aria-hidden="true" className="w-3 shrink-0">{isSelected ? '✓' : ''}</span>
                <span className="truncate">{r.label}</span>
              </button>
            );
          })}
        {hasMore && !isLoading && !error && (
          <p className="text-xs text-slate-400 px-1 py-2">
            More matches exist — keep typing to narrow the list.
          </p>
        )}
      </div>

      {selected.length > 0 && (
        <div className="border-t border-slate-200 mt-2 pt-2">
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-brand-blue hover:underline"
          >
            Clear {label.toLowerCase()}
          </button>
        </div>
      )}
    </>
  );
}

interface MultiSelectPopoverProps extends EntitySearchSource {
  /** Control label, e.g. "Workers". Also the accessible name of the trigger. */
  label: string;
  selected: SelectedEntity[];
  onChange: (next: SelectedEntity[]) => void;
}

/**
 * Presentational dropdown: a trigger showing the selection count wrapped around an
 * {@link EntityMultiSelectBody}. Deliberately unaware of what it's selecting —
 * {@link WorkerFilterSelect} and {@link ProjectFilterSelect} supply the data.
 */
function MultiSelectPopover({
  label,
  selected,
  onChange,
  ...source
}: MultiSelectPopoverProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={`border rounded px-2 py-1 text-sm flex items-center gap-1 ${
          selected.length > 0
            ? 'bg-brand-blue text-white border-brand-blue'
            : 'border-slate-300 text-brand-charcoal hover:bg-slate-100'
        }`}
      >
        <span>{selected.length > 0 ? `${label} (${selected.length})` : `All ${label.toLowerCase()}`}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={`Filter by ${label.toLowerCase()}`}
          className="absolute top-full left-0 mt-1 bg-white rounded-md shadow-lg border border-slate-200 p-2 w-[320px] z-30"
        >
          <EntityMultiSelectBody
            {...source}
            label={label}
            selected={selected}
            onChange={onChange}
            autoFocus
          />
        </div>
      )}
    </div>
  );
}

export function WorkerFilterSelect({
  selected,
  onChange,
}: {
  selected: SelectedEntity[];
  onChange: (next: SelectedEntity[]) => void;
}) {
  const source = useWorkerSearchSource();
  return <MultiSelectPopover {...source} label="Workers" selected={selected} onChange={onChange} />;
}

export function ProjectFilterSelect({
  selected,
  onChange,
}: {
  selected: SelectedEntity[];
  onChange: (next: SelectedEntity[]) => void;
}) {
  const source = useProjectSearchSource();
  return <MultiSelectPopover {...source} label="Projects" selected={selected} onChange={onChange} />;
}
